import { Title } from 'react-head'
import { useTr } from '../utils/useTr'
import { TrKey } from '../cnst/translations/trKey.cnst'
import { useApi } from '../utils/useApi'
import { useConfirmation, useToast, useWebsite } from '../utils/hooks'
import { ECommerceConfigFM, ProductFM } from '@bm-js/atlascloud-shared'
import FullScreenLoader from '../components/FullScreenLoader'
import { Button } from '@bm-js/atlascloud-ui'
import { useForm } from '../hooks/useForm'
import ProductForm from '../components/ProductForm'
import { useFetcher } from '../utils/fetcher'
import ProductCard from '../components/ProductCard'
import { useMemo } from 'react'

const EcommerceRoute = () => {
  const { tr } = useTr()
  const { anyWebsite, triggerRebuild } = useWebsite()
  const productForm = useForm({ formComponent: ProductForm })
  const fetcher = useFetcher()
  const toast = useToast()
  const confirmation = useConfirmation()

  const { data, isLoading, isError, revalidate } = useApi<{
    ecommerceConfig: ECommerceConfigFM
    products: ProductFM[]
  }>(`ecom-user/${anyWebsite?._id}`, {
    enabled: !!anyWebsite?._id,
  })

  const createProduct = async () => {
    await productForm({
      modalProps: { title: 'Skapa produkt', className: 'w-full max-w-2xl' },
      handler: async (data) => {
        const { err } = await fetcher({
          path: `ecom-user/${anyWebsite?._id}/products`,
          method: 'POST',
          body: data,
        })
        if (err) {
          toast.error(tr(TrKey.somethingWentWrong))
          return { ok: false, err }
        }
        toast.success('Produkt sparad')
        await revalidate()
        void triggerRebuild()
        return { ok: true }
      },
    })
  }

  const editProduct = async (product: ProductFM) => {
    await productForm({
      modalProps: { title: 'Redigera produkt', className: 'w-full max-w-2xl' },
      initialValue: product,
      handler: async (data) => {
        const { err } = await fetcher({
          path: `ecom-user/${anyWebsite?._id}/products/${product._id}`,
          method: 'PATCH',
          body: data,
        })
        if (err) {
          toast.error(tr(TrKey.somethingWentWrong))
          return { ok: false, err }
        }
        toast.success('Produkt sparad')
        await revalidate()
        void triggerRebuild()
        return { ok: true }
      },
    })
  }

  const deleteProduct = async (product: ProductFM) => {
    const confirmed = await confirmation({
      title: 'Radera produkt',
      message: 'Är du säker på att du vill radera produkten?',
    })
    if (!confirmed) return
    const { err } = await fetcher({
      path: `ecom-user/${anyWebsite?._id}/products/${product._id}`,
      method: 'DELETE',
    })
    if (err) {
      toast.error(tr(TrKey.somethingWentWrong))
      return
    }
    toast.success('Produkt raderad')
    await revalidate()
    void triggerRebuild()
  }

  const title = useMemo(() => {
    return tr(TrKey.ecommerceRouteTitle) + ' | Atlascloud'
  }, [tr])

  if (isLoading || (!data && !isError)) return <FullScreenLoader />
  if (isError || !data)
    return (
      <div className='content pb-20 text-center'>
        <Title>{title}</Title>
        <h2 className='m0 primblue'>{tr(TrKey.somethingWentWrong)}</h2>
      </div>
    )

  return (
    <div className='content pb-20'>
      <Title>{title}</Title>
      <div className='flex justify-between items-center'>
        <h1 className='m0 primblue l'>{tr(TrKey.ecommerceRouteTitle)}</h1>
        <Button onClick={createProduct}>Skapa produkt</Button>
      </div>
      <div className='mt-10 flex flex-col gap-4'>
        {data.products.length ? (
          data.products.map((product) => (
            <ProductCard
              key={product._id}
              product={product}
              editProduct={editProduct}
              deleteProduct={deleteProduct}
            />
          ))
        ) : (
          <p>Inga produkter hittades</p>
        )}
      </div>
    </div>
  )
}

export default EcommerceRoute
